import { useEffect } from 'react';
import {
  CONSUMER_CMS_SHOPPING_CART,
  useCmsConfig,
  IPortabilityMobileCMS,
  productSpecificError,
  useGetPortabilityInfoMobile,
  errorMock,
  IGenericErrorCMS,
  IDigitalOperatorsConfig,
  ICommonLabelsInStepper,
  organizeCommonLabelsInStepper,
} from '@vfit/consumer/data-access';
import { API, regex, defineYupSchema, getClickToCallWithKeyOrUrl } from '@vfit/shared/data-access';
import { ShadowsGroup } from '@vfit/shared/components';
import {
  organizePortabilityError,
  organizePortabilityNumberCard,
} from './portabilityNumberCard.utils';
import PortabilityNumber from './components/PortabilityNumber/portabilityNumber';
import { ICheckoutData } from '../../checkout.models';
import { ID_FLOWS } from '../../checkout.constants';
import { Skeleton } from './portabilityNumberCard.style';
import { DEFAULT_PORTABILITY } from '../../../iBuyMobile.context.data';
import { useCheckout } from '../../../iBuyMobile.context';
import TaggingPortabilityNumberCard from './portabilityNumberCard.tagging';
import { useQueryClient } from 'react-query';

const PortabilityNumberCard = ({
  handleOnChangeEnableGoNext,
  handleGoPrevSlide,
}: ICheckoutData) => {
  const {
    currentStepKey,
    checkoutErrors,
    portability,
    isModalButtonSticky,
    product,
    setPortability,
    setCurrentStepKey,
    setIsValidNumberPortability,
  } = useCheckout();
  const queryClient = useQueryClient();
  const isRequiredPortability = product?.isRequiredPortability;
  const { buttonGenericError, urlGenericErrorCallMeNow } = productSpecificError(product);
  const portabilityNumberCardCMS = useCmsConfig(
    CONSUMER_CMS_SHOPPING_CART,
    API.CMS_GET_PORTABILITY_MOBILE
  ) as IPortabilityMobileCMS;
  const genericError = useCmsConfig(
    CONSUMER_CMS_SHOPPING_CART,
    API.CMS_GET_GENERIC_ERROR_MOBILE
  ) as IGenericErrorCMS;
  const { blacklist } =
    (useCmsConfig(
      CONSUMER_CMS_SHOPPING_CART,
      API.CMS_GET_DIGITAL_BUNDLE_OPERATORS
    ) as IDigitalOperatorsConfig) || {};
  const commonLabels = useCmsConfig(
    CONSUMER_CMS_SHOPPING_CART,
    API.CMS_GET_STEPPER_COMMON_LABELS_MOBILE
  ) as ICommonLabelsInStepper;
  const { goBackInStepper } = organizeCommonLabelsInStepper(commonLabels);
  const { title, description, label, disclaimer, validationMessage } =
    organizePortabilityNumberCard(portabilityNumberCardCMS);
  const { titleError, messageError } = organizePortabilityError(portabilityNumberCardCMS);

  const {
    isLoading: isLoadingPortInfo,
    isError: isErrorPortInfo,
    error: errorPortIn,
  } = useGetPortabilityInfoMobile(
    portabilityNumberCardCMS.portabilitymobile.operatorList,
    portabilityNumberCardCMS.portabilitymobile.operatorsOrder,
    blacklist?.elements
  );

  const checkPortabilityValidation = () => {
    if (portability?.portabilityNumber) {
      const isMatches = portability.portabilityNumber.match(regex['mobilePhoneNumber']);
      handleOnChangeEnableGoNext?.(!!isMatches);
    }
  };

  useEffect(() => {
    if (isErrorPortInfo) {
      const action = () => {
        if (handleGoPrevSlide) handleGoPrevSlide();
      };
      const mockError = errorMock(
        'portininfo',
        errorPortIn,
        undefined,
        undefined,
        'portin info in portabilityCard',
        false,
        {
          portabilityNumber: portability?.portabilityNumber || '',
          portabilityOperators: portability?.portabilityOperators || '',
        }
      );
      checkoutErrors?.showAndTrackError?.(
        genericError,
        action,
        mockError,
        titleError,
        messageError,
        action,
        goBackInStepper,
        () =>
          getClickToCallWithKeyOrUrl(
            queryClient,
            urlGenericErrorCallMeNow,
            product,
            action,
            currentStepKey,
            mockError
          ),
        product?.removeCTC ? undefined : buttonGenericError,
        isModalButtonSticky,
        true
      );
    }
  }, [isErrorPortInfo]);

  useEffect(() => {
    checkPortabilityValidation();
  }, [portability]);

  useEffect(() => {
    setCurrentStepKey(ID_FLOWS.PORTABILITY_NUMBER);
    checkPortabilityValidation();
    if (isRequiredPortability) {
      localStorage.setItem('portability', '1');
    }
  }, []);

  const onChangeData = (telephoneNumber: string) => {
    setIsValidNumberPortability(false);
    setPortability({
      ...DEFAULT_PORTABILITY,
      portabilityWant: isRequiredPortability ? true : portability.portabilityWant,
      portabilityNumber: telephoneNumber || '',
    });
  };

  return (
    <>
      <TaggingPortabilityNumberCard />
      {isLoadingPortInfo && (
        <Skeleton>
          <ShadowsGroup heights={70} quantity={3} />
        </Skeleton>
      )}
      {!isLoadingPortInfo && (
        <PortabilityNumber
          validationMessage={validationMessage}
          title={title}
          description={description}
          label={label}
          disclaimer={disclaimer}
          value={portability.portabilityNumber}
          onChangeData={onChangeData}
          disabled={false}
          schema={defineYupSchema('phoneNumber')}
        />
      )}
    </>
  );
};

export default PortabilityNumberCard;

